import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  FileText, AlertTriangle, DollarSign, Package, TrendingUp,
  Shield, Eye, Users, Clock, Download
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const US_STATES = [
  { code: 'TX', name: 'Texas' }, { code: 'CA', name: 'California' }, { code: 'NY', name: 'New York' },
  { code: 'FL', name: 'Florida' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'OH', name: 'Ohio' }, { code: 'PA', name: 'Pennsylvania' },
];

export default function ComplianceReports() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [activeTab, setActiveTab] = useState('tobacco');
  const [loading, setLoading] = useState(false);

  // Tobacco Tax
  const [stateCode, setStateCode] = useState('TX');
  const [tobaccoReport, setTobaccoReport] = useState(null);

  // AI Smart Fraud Prevention
  const [days, setDays] = useState(7);
  const [cashierReport, setCashierReport] = useState(null);

  const loadTobaccoReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/compliance/tobacco-tax-report?state_code=${stateCode}`, { headers });
      setTobaccoReport(res.data);
    } catch (err) {
      toast.error('Failed to load tobacco tax report');
    } finally { setLoading(false); }
  }, [stateCode, token]);

  const loadCashierReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/compliance/cashier-report?days=${days}`, { headers });
      setCashierReport(res.data);
    } catch (err) {
      toast.error('Failed to load cashier report');
    } finally { setLoading(false); }
  }, [days, token]);

  useEffect(() => {
    if (activeTab === 'tobacco') loadTobaccoReport();
    else loadCashierReport();
  }, [activeTab, loadTobaccoReport, loadCashierReport]);

  return (
    <div className="space-y-6" data-testid="compliance-reports-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Shield className="h-6 w-6 text-red-500" />
          Compliance & Security
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Tobacco tax reports & employee accountability</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2" data-testid="compliance-tabs">
        <button onClick={() => setActiveTab('tobacco')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'tobacco' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'
          }`} data-testid="tab-tobacco">
          <FileText className="h-4 w-4" /> Tobacco Tax
        </button>
        <button onClick={() => setActiveTab('theft')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'theft' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'
          }`} data-testid="tab-theft">
          <Eye className="h-4 w-4" /> AI Fraud Prevention
        </button>
      </div>

      {/* ============ TOBACCO TAX TAB ============ */}
      {activeTab === 'tobacco' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Select value={stateCode} onValueChange={(v) => { setStateCode(v); }}>
              <SelectTrigger className="w-48" data-testid="state-select">
                <SelectValue placeholder="Select State" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map(s => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={loadTobaccoReport} disabled={loading} variant="outline" className="gap-2">
              <TrendingUp className="h-4 w-4" /> Generate Report
            </Button>
          </div>

          {tobaccoReport && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="tobacco-summary">
                <Card className="bg-card border-border">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Tobacco Revenue</p>
                    <p className="text-2xl font-bold text-emerald-500">${tobaccoReport.summary.total_tobacco_revenue.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Units Sold</p>
                    <p className="text-2xl font-bold text-blue-500">{tobaccoReport.summary.total_tobacco_units}</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Excise Tax Est.</p>
                    <p className="text-2xl font-bold text-red-500">${tobaccoReport.summary.estimated_excise_tax.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Tax/Pack ({tobaccoReport.state})</p>
                    <p className="text-2xl font-bold text-amber-500">${tobaccoReport.summary.cigarette_tax_rate_per_pack}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Category Breakdown */}
              <Card className="bg-card border-border" data-testid="tobacco-breakdown">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Package className="h-5 w-5 text-amber-500" /> Sales by Category
                  </h3>
                  {tobaccoReport.categories.length > 0 ? (
                    <div className="space-y-4">
                      {tobaccoReport.categories.map((cat, idx) => (
                        <div key={idx}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-foreground">{cat.category}</span>
                            <span className="text-sm text-muted-foreground">{cat.total_units} units — ${cat.total_revenue.toLocaleString()}</span>
                          </div>
                          <div className="pl-4 space-y-1">
                            {cat.products.map((p, i) => (
                              <div key={i} className="flex justify-between text-sm text-muted-foreground">
                                <span>{p.name}</span>
                                <span>{p.units} units — ${p.revenue.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-6">No tobacco sales in this period</p>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* ============ THEFT PREVENTION TAB ============ */}
      {activeTab === 'theft' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Select value={days.toString()} onValueChange={(v) => setDays(parseInt(v))}>
              <SelectTrigger className="w-40" data-testid="days-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Today</SelectItem>
                <SelectItem value="7">Last 7 Days</SelectItem>
                <SelectItem value="14">Last 14 Days</SelectItem>
                <SelectItem value="30">Last 30 Days</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={loadCashierReport} disabled={loading} variant="outline" className="gap-2">
              <Eye className="h-4 w-4" /> Refresh
            </Button>
          </div>

          {cashierReport && (
            <>
              {/* Alert Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="theft-summary">
                <Card className={cashierReport.flags_summary.flagged_cashiers > 0 ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30' : 'bg-card border-border'}>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Flagged Cashiers</p>
                    <p className={`text-2xl font-bold ${cashierReport.flags_summary.flagged_cashiers > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                      {cashierReport.flags_summary.flagged_cashiers}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Voids</p>
                    <p className="text-2xl font-bold text-amber-500">{cashierReport.security_events.total_voids}</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Void Amount</p>
                    <p className="text-2xl font-bold text-red-500">${cashierReport.security_events.total_void_amount}</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">No-Sale Opens</p>
                    <p className="text-2xl font-bold text-purple-500">{cashierReport.security_events.no_sale_opens}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Cashier Breakdown */}
              <Card className="bg-card border-border" data-testid="cashier-breakdown">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-500" /> Cashier Accountability
                  </h3>
                  {cashierReport.cashiers.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border text-muted-foreground text-left">
                            <th className="px-3 py-2 font-medium">Cashier</th>
                            <th className="px-3 py-2 font-medium text-right">Sales</th>
                            <th className="px-3 py-2 font-medium text-right">Txns</th>
                            <th className="px-3 py-2 font-medium text-right">Avg</th>
                            <th className="px-3 py-2 font-medium text-right">Voids</th>
                            <th className="px-3 py-2 font-medium text-right">Discounts</th>
                            <th className="px-3 py-2 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cashierReport.cashiers.map((c, idx) => (
                            <tr key={idx} className={`border-b border-border/50 ${c.is_flagged ? 'bg-red-50 dark:bg-red-950/20' : ''}`}>
                              <td className="px-3 py-2 font-medium text-foreground">{c.name}</td>
                              <td className="px-3 py-2 text-right text-emerald-600">${c.total_sales.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right text-foreground">{c.transaction_count}</td>
                              <td className="px-3 py-2 text-right text-muted-foreground">${c.avg_transaction}</td>
                              <td className="px-3 py-2 text-right">
                                <span className={c.void_count >= 3 ? 'text-red-500 font-bold' : 'text-foreground'}>
                                  {c.void_count} (${c.void_amount})
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right text-foreground">{c.discount_count} (${c.discount_amount})</td>
                              <td className="px-3 py-2">
                                {c.is_flagged ? (
                                  <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 text-xs rounded-full font-semibold flex items-center gap-1 w-fit">
                                    <AlertTriangle className="h-3 w-3" /> Flagged
                                  </span>
                                ) : (
                                  <span className="text-xs text-emerald-600">Clean</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-6">No cashier data for this period</p>
                  )}
                </CardContent>
              </Card>

              {/* Shift Over/Short */}
              {cashierReport.shift_variances.length > 0 && (
                <Card className="bg-card border-border" data-testid="shift-variances">
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-amber-500" /> Cash Drawer Over/Short
                    </h3>
                    <div className="space-y-2">
                      {cashierReport.shift_variances.map((sv, idx) => (
                        <div key={idx} className={`flex items-center justify-between p-3 rounded-lg border ${
                          sv.is_short ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20' : 'border-border bg-muted/30'
                        }`}>
                          <div>
                            <span className="font-medium text-sm text-foreground">{sv.cashier}</span>
                            <span className="text-xs text-muted-foreground ml-2">{sv.date}</span>
                          </div>
                          <div className="text-right">
                            <span className={`font-bold ${sv.variance < 0 ? 'text-red-500' : sv.variance > 0 ? 'text-blue-500' : 'text-emerald-500'}`}>
                              {sv.variance >= 0 ? '+' : ''}${sv.variance.toFixed(2)}
                            </span>
                            <p className="text-[10px] text-muted-foreground">Expected: ${sv.expected} | Actual: ${sv.actual}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
